import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import Alert from '../components/Alert'
import Spinner from '../components/Spinner'
import DepartmentSetup from '../components/DepartmentSetup'
import { confirmEmail, resendConfirmEmail } from '../api/auth'
import { authErrorMessage } from '../lib/errors'

const titles = {
  verifying: 'Confirming your email',
  success: 'Email confirmed',
  error: 'Confirmation failed',
  
}

const subtitles = {
  verifying: 'Hold on while we activate your account.',
  success: 'Your email is confirmed. An admin still needs to approve your account.',
  error: 'That link did not work. Request a new one below.',
}

export default function ConfirmEmail() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''

  const [state, setState] = useState('verifying')
  const [alert, setAlert] = useState(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [busy, setBusy] = useState(false)
  const [resent, setResent] = useState(false)
  // Department Manager signups pick their departments right after confirming.
  const [setupToken, setSetupToken] = useState(null)

  // StrictMode runs mount effects twice in dev; the ref survives the remount so
  // the POST fires once. The server is idempotent either way.
  const firedRef = useRef(false)

  const runConfirm = useCallback(async () => {
    if (!token) {
      setState('error')
      setAlert({ type: 'danger', message: 'This confirmation link is missing its token.' })
      return
    }
    try {
      const res = await confirmEmail(token)
      const data = res?.data || {}
      if (data.email) setEmail(data.email)
      if (data.setup_token) setSetupToken(data.setup_token)
      setState('success')
      const needsApproval = data.is_approved === false
      setAlert({
        type: 'success',
        message: needsApproval
          ? (data.already_confirmed
              ? 'Your email was already confirmed. Your Approval is at Pending.'
              : 'Your email is confirmed. Your Approval is at Pending.')
          : (data.already_confirmed
              ? 'Your email was already confirmed. You can sign in.'
              : 'Your email is confirmed. You can sign in now.'),
      })
    } catch (err) {
      setState('error')
      setAlert({ type: 'danger', message: authErrorMessage(err, 'Could not confirm your email.') })
    }
  }, [token])

  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    runConfirm()
  }, [runConfirm])

  async function onResend(e) {
    e.preventDefault()
    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }
    setEmailError('')
    setAlert(null)
    setBusy(true)
    try {
      await resendConfirmEmail(email.trim())
      setResent(true)
      setAlert({ type: 'success', message: 'We sent a new confirmation link to your email.' })
    } catch (err) {
      setAlert({ type: 'danger', message: authErrorMessage(err, 'Could not send a new link.') })
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title={titles[state]}
      subtitle={subtitles[state]}
      foot={
        <>
          Back to{' '}
          <Link className="link-btn" to="/auth/login">
            Sign in
          </Link>
        </>
      }
    >
      <Alert type={alert?.type}>{alert?.message}</Alert>

      {state === 'verifying' && (
        <p className="countdown" aria-live="polite">
          <Spinner label="Confirming your email" /> Confirming…
        </p>
      )}

      {state === 'success' && setupToken && <DepartmentSetup setupToken={setupToken} />}

      {state === 'success' && !setupToken && (
        <Link className="btn btn-primary btn-block" to="/auth/login" style={{ textDecoration: 'none' }}>
          Sign in
        </Link>
      )}

      {state === 'error' && !resent && (
        <form onSubmit={onResend} noValidate>
          <div className="form-field">
            <label htmlFor="confirm-email">
              Email<span className="req"> *</span>
            </label>
            <input
              id="confirm-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setEmailError('')
              }}
              className={emailError ? 'err' : ''}
              placeholder="you@utopiabrands.com"
              disabled={busy}
              required
            />
            <span className={`field-error${emailError ? ' show' : ''}`}>{emailError || ''}</span>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {busy ? <Spinner label="Sending link" /> : null}
            {busy ? 'Sending…' : 'Send a new link'}
          </button>
        </form>
      )}
    </AuthLayout>
  )
}
